<script setup>
import { ref, onUnmounted } from 'vue'

/**
 * AshField — the breathing air of the saga, on a single canvas.
 *
 * One particle field, three states blended by a continuous `mood`
 * scrubbed by the parent's master timeline:
 *   0 — cold ash sinking through a silent world
 *   1 — warm motes rising as the world heals
 *   2 — near-still stardust around the observatory
 *
 * The parent starts/stops the loop (never started under reduced
 * motion) and feeds mood from GSAP's onUpdate — no Vue reactivity in
 * the hot path.
 */
const canvasRef = ref(null)

let ctx = null
let raf = 0
let running = false
let last = 0
let mood = 0
let particles = []
let W = 0
let H = 0

const lerp = (a, b, t) => a + (b - a) * t
const clamp01 = (v) => Math.min(1, Math.max(0, v))

// cold ash → (gold | violet) motes → stardust
const COLD = [148, 158, 190]
const GOLD = [255, 217, 162]
const VIOLET = [183, 163, 246]
const STAR = [217, 201, 255]

function resize() {
  const canvas = canvasRef.value
  if (!canvas) return
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
  W = canvas.clientWidth
  H = canvas.clientHeight
  canvas.width = Math.round(W * dpr)
  canvas.height = Math.round(H * dpr)
  ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)

  const count = Math.min(130, Math.round((W * H) / 14000))
  particles = Array.from({ length: count }, () => spawn(true))
}

function spawn(anywhere) {
  return {
    x: Math.random() * W,
    y: anywhere ? Math.random() * H : H + 6,
    r: 0.6 + Math.random() * 1.3,
    fall: 10 + Math.random() * 14, // px/s when cold
    rise: 6 + Math.random() * 10, // px/s when warm
    sway: 6 + Math.random() * 14,
    phase: Math.random() * Math.PI * 2,
    twinkle: 2 + Math.random() * 3,
    warm: Math.random() // 0 → violet mote, 1 → gold mote
  }
}

function tick(now) {
  if (!running) return
  const dt = Math.min((now - last) / 1000, 0.05)
  last = now
  const t = now / 1000

  ctx.clearRect(0, 0, W, H)

  const heal = clamp01(mood) // 0 cold → 1 warm
  const still = clamp01(mood - 1) // 0 warm → 1 stardust
  const vy = lerp(lerp(1, -0.8, heal), -0.06, still)

  for (const p of particles) {
    p.y += vy * p.fall * dt
    p.x += Math.sin(t * 0.5 + p.phase) * p.sway * dt

    if (p.y > H + 8) { p.y = -8; p.x = Math.random() * W }
    if (p.y < -8) { p.y = H + 8; p.x = Math.random() * W }
    if (p.x > W + 8) p.x = -8
    if (p.x < -8) p.x = W + 8

    const warmC = p.warm > 0.45 ? GOLD : VIOLET
    const c0 = COLD.map((v, i) => lerp(v, warmC[i], heal))
    const c = c0.map((v, i) => Math.round(lerp(v, STAR[i], still)))

    const breathe = 0.55 + 0.45 * Math.sin(t * p.twinkle + p.phase)
    const alpha = lerp(0.32, 0.5, heal) * lerp(0.9, breathe, Math.max(heal * 0.5, still))

    ctx.beginPath()
    ctx.arc(p.x, p.y, p.r * lerp(1, 0.75, still), 0, Math.PI * 2)
    ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${alpha.toFixed(3)})`
    ctx.fill()
  }

  raf = requestAnimationFrame(tick)
}

function onVisibility() {
  if (document.hidden) {
    cancelAnimationFrame(raf)
  } else if (running) {
    last = performance.now()
    raf = requestAnimationFrame(tick)
  }
}

function start() {
  if (running || !canvasRef.value) return
  resize()
  running = true
  last = performance.now()
  raf = requestAnimationFrame(tick)
  window.addEventListener('resize', resize)
  document.addEventListener('visibilitychange', onVisibility)
}

function stop() {
  running = false
  cancelAnimationFrame(raf)
  window.removeEventListener('resize', resize)
  document.removeEventListener('visibilitychange', onVisibility)
}

function setMood(v) {
  mood = v
}

onUnmounted(stop)

defineExpose({ start, stop, setMood })
</script>

<template>
  <canvas ref="canvasRef" class="pointer-events-none w-full h-full" aria-hidden="true"></canvas>
</template>
